import React from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { ChevronLeft, Bell } from "lucide-react";
import { useApp } from "../contexts/AppContext";
import { eventsAPI } from "../services/api";
import "./Header.css";

const pageTitles = {
  programme: "Programme",
  speakers: "Speakers",
  "my-schedule": "My Schedule",
  masterclasses: "Masterclasses",
  "my-qr": "My QR Code",
  venue: "Venue Map",
  notifications: "Announcements",
  help: "Help & Support",
  profile: "Profile",
  survey: "Event Survey",
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams();
  const { unreadCount, currentEvent, setCurrentEvent } = useApp();

  // Fetch event details if we are in an event scope but don't have the data
  React.useEffect(() => {
    const fetchEventDetails = async () => {
      if (eventId && (!currentEvent || currentEvent.id !== parseInt(eventId))) {
        try {
          const res = await eventsAPI.getById(eventId);
          setCurrentEvent(res.data);
        } catch (err) {
          console.error("Failed to fetch event branding", err);
        }
      }
    };

    fetchEventDetails();
  }, [eventId, currentEvent, setCurrentEvent]);

  // Helper to format date range: "7–8 February 2026"
  const formatDateRange = (startStr, endStr) => {
    if (!startStr) return "";
    const start = new Date(startStr);
    const end = endStr ? new Date(endStr) : null;

    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    if (!end || start.toDateString() === end.toDateString()) {
      return `${start.getDate()} ${monthNames[start.getMonth()]} ${start.getFullYear()}`;
    }

    if (start.getMonth() === end.getMonth()) {
      return `${start.getDate()}–${end.getDate()} ${monthNames[start.getMonth()]} ${start.getFullYear()}`;
    }

    return `${start.getDate()} ${monthNames[start.getMonth()]} – ${end.getDate()} ${monthNames[end.getMonth()]} ${start.getFullYear()}`;
  };

  // Extract the segment after the eventId or root
  // URL: /event/123/programme -> programme
  // URL: /programme -> programme (old style just in case)
  const segments = location.pathname.split("/");
  // ['', 'event', '123', 'programme']
  let lastSegment = segments[segments.length - 1];

  // Handle /event/123 case (Home)
  if (eventId && location.pathname === `/event/${eventId}`) {
    lastSegment = "";
  }

  // Iterate to find matching title in case of nested routes (e.g. /speakers/1)
  let title = "RF Conference";
  for (const [key, value] of Object.entries(pageTitles)) {
    if (location.pathname.includes(key)) {
      title = value;
      break;
    }
  }

  const canGoBack =
    location.pathname !== "/" &&
    location.pathname !== `/event/${eventId}` &&
    location.key !== "default";

  const handleBack = () => {
    if (canGoBack) {
      navigate(-1);
    } else {
      navigate(eventId ? `/event/${eventId}` : "/");
    }
  };

  return (
    <header className="header">
      <div className="header-left">
        {location.pathname !== "/" &&
          location.pathname !== `/event/${eventId}` && (
            <button
              className="header-btn"
              onClick={handleBack}
              aria-label="Go back"
            >
              <ChevronLeft size={24} />
            </button>
          )}
      </div>

      {eventId && location.pathname === `/event/${eventId}` ? (
        <div className="header-title-complex">
          <h1 className="event-main-title">
            {currentEvent?.title || "Conference App"}
          </h1>
          <div className="event-subtitles">
            <span className="event-subtitle">
              {currentEvent ? formatDateRange(currentEvent.start_date, currentEvent.end_date) : "Loading dates..."}
            </span>
            <span className="event-subtitle">
              {currentEvent?.location || "Reliance Foundation"}
            </span>
          </div>
        </div>
      ) : location.pathname.includes("programme") ? (
        <div className="header-title-complex">
          <h1 className="header-title" style={{ flex: "none" }}>
            {title}
          </h1>
          <span
            className="event-subtitle"
            style={{ fontSize: "0.75rem", fontWeight: 400, marginTop: "2px" }}
          >
            View full agenda
          </span>
        </div>
      ) : location.pathname.includes("my-schedule") ? (
        <div className="header-title-complex">
          <h1 className="header-title" style={{ flex: "none" }}>
            {title}
          </h1>
          <span
            className="event-subtitle"
            style={{ fontSize: "0.75rem", fontWeight: 400, marginTop: "2px" }}
          >
            Your registered masterclasses and saved sessions
          </span>
        </div>
      ) : location.pathname.includes("speakers") ? (
        <div className="header-title-complex">
          <h1 className="header-title" style={{ flex: "none" }}>
            {title}
          </h1>
          <span
            className="event-subtitle"
            style={{ fontSize: "0.75rem", fontWeight: 400, marginTop: "2px" }}
          >
            Meet the experts
          </span>
        </div>
      ) : location.pathname.includes("feedback") ? (
        <div className="header-title-complex">
          <h1 className="header-title" style={{ flex: "none" }}>
            Question and Answers
          </h1>
          <span
            className="event-subtitle"
            style={{ fontSize: "0.75rem", fontWeight: 400, marginTop: "2px" }}
          >
            Share feedback
          </span>
        </div>
      ) : (
        <h1 className="header-title">{title}</h1>
      )}

      <div className="header-right">
        {!location.pathname.includes("notifications") && (
          <button
            className="header-btn notification-btn"
            onClick={() =>
              navigate(
                eventId ? `/event/${eventId}/notifications` : "/notifications",
              )
            }
            aria-label="Notifications"
          >
            {/* <Bell size={22} /> */}
            {unreadCount > 0 && (
              <span className="notification-badge">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
        )}
      </div>
    </header>
  );
};

export default Header;
